import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { safeParseDate } from '../../utils/dateUtils';
import { getIsoWeekNumber, getIsoWeek } from '../../utils/isoDates';

const formatYMD = (d) => new Date(d).toISOString().split('T')[0];

// Normalize any date-like value to a UTC day (midnight UTC based on its ISO day).
// This avoids local-time shifts around midnight and keeps day/week bucketing consistent.
const toUtcDay = (dateValue) => {
  const d = safeParseDate(dateValue);
  if (!d) return null;
  const isoDay = d.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)
  const [y, m, day] = isoDay.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day));
};

const startOfDayUTC = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
const endOfDayUTC = (d) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));

function buildDailyBuckets(start, end) {
  const counts = new Map();
  const cursor = startOfDayUTC(start);
  const endCursor = startOfDayUTC(end);
  while (cursor <= endCursor) {
    const key = formatYMD(cursor);
    counts.set(key, 0);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return counts;
}

function buildWeeklyBuckets(start, end) {
  const counts = new Map();
  const set = new Set();
  const cursor = startOfDayUTC(start);
  const endCursor = startOfDayUTC(end);
  while (cursor <= endCursor) {
    set.add(getIsoWeekNumber(cursor).fullWeekString);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  Array.from(set.values()).sort((a, b) => a.localeCompare(b)).forEach((k) => counts.set(k, 0));
  return counts;
}

const WeeklyTick = ({ x, y, payload }) => {
  const m = String(payload?.value || '').match(/^(\d{4})-W(\d{2})$/);
  if (!m) {
    return (
      <text x={x} y={y} dy={16} textAnchor="middle" fill="rgb(107, 114, 128)" fontSize={11}>
        {payload?.value}
      </text>
    );
  }
  const year = m[1];
  const ww = m[2];
  const showYear = ww === '01';

  return (
    <text x={x} y={y} textAnchor="middle" fill="rgb(107, 114, 128)" fontSize={11}>
      <tspan x={x} dy="12">{`W${ww}`}</tspan>
      {showYear && <tspan x={x} dy="12">{year}</tspan>}
    </text>
  );
};

const GIAnswersHistogram = ({
  answers = [],
  startDate,
  endDate,
  title = 'Réponses',
  height = 260
}) => {
  const { chartData, mode, total, max } = useMemo(() => {
    if (!answers?.length) return { chartData: [], mode: 'daily', total: 0, max: 0 };

    const parsed = answers
      .map(a => toUtcDay(a.AnswerDate))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());

    if (parsed.length === 0) return { chartData: [], mode: 'daily', total: 0, max: 0 };

    const effectiveStart = startDate ? toUtcDay(startDate) : parsed[0];
    const effectiveEnd = endDate ? toUtcDay(endDate) : parsed[parsed.length - 1];

    if (!effectiveStart || !effectiveEnd) return { chartData: [], mode: 'daily', total: 0, max: 0 };

    const s = startOfDayUTC(effectiveStart);
    const e = startOfDayUTC(effectiveEnd);
    const diffDays = Math.floor((e.getTime() - s.getTime()) / 86400000) + 1; // inclusive
    const mode = diffDays < 90 ? 'daily' : 'weekly';

    const buckets = mode === 'daily' ? buildDailyBuckets(s, e) : buildWeeklyBuckets(s, e);

    answers.forEach((answer) => {
      const d = toUtcDay(answer.AnswerDate);
      if (!d) return;
      const key = mode === 'daily' ? formatYMD(d) : getIsoWeekNumber(d).fullWeekString;
      if (!buckets.has(key)) return;
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });

    const chartData = Array.from(buckets.entries()).map(([label, count]) => {
      if (mode === 'weekly') {
        const m = label.match(/^(\d{4})-W(\d{2})$/);
        if (m) {
          const year = Number(m[1]);
          const weekNumber = Number(m[2]);
          const { startDate: ws, endDate: we } = getIsoWeek(weekNumber, year);
          return { label, count, rangeStart: formatYMD(ws), rangeEnd: formatYMD(we) };
        }
      }
      return { label, count, rangeStart: label, rangeEnd: label };
    });

    const total = chartData.reduce((acc, r) => acc + (Number(r.count) || 0), 0);
    const max = chartData.reduce((acc, r) => Math.max(acc, Number(r.count) || 0), 0);

    return { chartData, mode, total, max };
  }, [answers, startDate, endDate]);

  if (!chartData.length) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title} par {mode === 'daily' ? 'jour' : 'semaine'}
          </h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {total} réponse{total > 1 ? 's' : ''} • max {max}
          </div>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Granularité: {mode === 'daily' ? 'Journalière (< 90 jours)' : 'Hebdomadaire (≥ 90 jours)'}
        </div>
      </div>

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              interval="preserveStartEnd"
              tick={mode === 'weekly' ? <WeeklyTick /> : { fontSize: 11, fill: 'rgb(107, 114, 128)' }}
              tickFormatter={mode === 'weekly' ? undefined : (val) => {
                const d = new Date(val);
                return Number.isNaN(d.getTime())
                  ? val
                  : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
              }}
              height={mode === 'weekly' ? 38 : undefined}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgb(107, 114, 128)' }} />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.05)' }}
              labelFormatter={(label, payload) => {
                const row = payload?.[0]?.payload;
                if (!row) return label;
                if (mode === 'weekly') {
                  const m = String(label).match(/^(\d{4})-W(\d{2})$/);
                  const pretty = m ? `W${m[2]} (${m[1]})` : String(label);
                  return `Semaine ${pretty} • ${row.rangeStart} → ${row.rangeEnd}`;
                }
                return new Date(label).toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              }}
              formatter={(value) => {
                const n = Number(value) || 0;
                return [`${n} réponse${n > 1 ? 's' : ''}`, ''];
              }}
            />
            <Bar dataKey="count" name="Réponses" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GIAnswersHistogram;

